import React from 'react';

/**
 * Shared skeleton primitives and composites.
 *
 * Shimmer placeholders that match the shape of real content (campaign cards,
 * lead cards, table rows, modal shells) so users see the layout immediately
 * while data loads — instead of a blank screen with a tiny spinner.
 *
 * The shimmer uses `animate-pulse` (opacity fade, 2s) on translucent white
 * blocks — the same recipe proven in the ISR Workspace's hydrating state.
 * All primitives respect `prefers-reduced-motion` via `motion-reduce:animate-none`.
 */

/** A single shimmering line/block. The base primitive. */
export const SkeletonLine: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    aria-hidden="true"
    className={`block animate-pulse rounded bg-white/[0.07] motion-reduce:animate-none ${className}`}
  />
);

/** A larger rectangular block — for avatar tiles, hero regions, thumbnails. */
export const SkeletonBlock: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    aria-hidden="true"
    className={`animate-pulse rounded bg-white/[0.07] motion-reduce:animate-none ${className}`}
  />
);

/**
 * A section of stacked lines mimicking a paragraph or labelled field group.
 * Used inside the lead-detail modal skeleton and the ISR hydrating state.
 */
export const SkeletonSection: React.FC<{ label?: string; lines?: number; className?: string }> = ({
  label = true,
  lines = 3,
  className = '',
}) => (
  <section className={`py-4 first:pt-0 ${className}`} aria-busy="true">
    {label && (
      <div className="mb-2 flex items-baseline gap-3">
        <SkeletonLine className="h-3 w-24" />
        <SkeletonLine className="h-2.5 w-32" />
      </div>
    )}
    <SkeletonLine className="h-3 w-full max-w-[80ch]" />
    {Array.from({ length: lines - 1 }).map((_, index) => (
      <SkeletonLine key={index} className={`mt-2 h-3 ${index === lines - 2 ? 'w-2/3' : 'w-5/6'}`} />
    ))}
  </section>
);

/**
 * A card-shaped skeleton matching the campaign/lead card layout:
 * rounded-2xl, dark gradient bg, header row (avatar + title + status pill),
 * body lines. Reads as a "campaign card" or "lead card" at a glance.
 */
export const SkeletonCard: React.FC<{ viewMode?: 'grid-simple' | 'grid-viewcard' | 'list' }> = ({
  viewMode = 'grid-viewcard',
}) => (
  <div
    aria-hidden="true"
    className="relative overflow-hidden rounded-2xl border border-white/5 bg-[linear-gradient(160deg,#121212,#0a0a0a)] p-5 shadow-[0_22px_45px_rgba(0,0,0,0.28)]"
  >
    {/* Header row: avatar circle + title + status pill */}
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <SkeletonBlock className="h-10 w-10 shrink-0 rounded-xl" />
        <div className="flex flex-col gap-1.5">
          <SkeletonLine className="h-3.5 w-40" />
          <SkeletonLine className="h-2.5 w-28" />
        </div>
      </div>
      <SkeletonLine className="h-5 w-16 rounded-full" />
    </div>

    {/* Body */}
    <div className="mt-4 space-y-2">
      <SkeletonLine className="h-2.5 w-full" />
      <SkeletonLine className="h-2.5 w-11/12" />
      {viewMode !== 'grid-simple' && <SkeletonLine className="h-2.5 w-4/5" />}
    </div>

    {/* Footer metric chips */}
    <div className="mt-4 flex items-center gap-2">
      <SkeletonLine className="h-6 w-20 rounded-lg" />
      <SkeletonLine className="h-6 w-16 rounded-lg" />
      <SkeletonLine className="h-6 w-14 rounded-lg" />
    </div>
  </div>
);

/**
 * A responsive grid of skeleton cards. Uses the same grid classes as the
 * real campaigns/leads grids so there's no layout jump when content arrives.
 */
export const SkeletonGrid: React.FC<{
  count?: number;
  viewMode?: 'grid-simple' | 'grid-viewcard' | 'list';
}> = ({ count = 9, viewMode = 'grid-viewcard' }) => (
  <div
    className={`grid gap-6 ${
      viewMode === 'grid-simple'
        ? 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4'
        : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    }`}
    aria-hidden="true"
  >
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} viewMode={viewMode} />
    ))}
  </div>
);

/** A table shell of skeleton rows for the list view. */
/**
 * A table-shell skeleton matching the campaign/lead list view: a rounded
 * container with a header row + N data rows. Uses a real <table> so it
 * matches the DOM shape and column rhythm of the real list.
 */
export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 9 }) => (
  <div
    className="overflow-hidden rounded-[24px] border border-white/8 bg-[linear-gradient(180deg,rgba(14,14,18,0.98),rgba(10,10,12,0.94))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]"
    aria-hidden="true"
  >
    <table className="w-full table-fixed border-collapse text-left">
      <colgroup>
        <col className="w-[44px]" />
        <col className="w-[35%]" />
        <col className="w-[13%]" />
        <col className="w-[23%]" />
        <col className="w-[11%]" />
        <col className="w-[18%]" />
      </colgroup>
      <thead>
        <tr className="border-b border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01))]">
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-4" /></th>
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-32" /></th>
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-20" /></th>
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-24" /></th>
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-16" /></th>
          <th className="py-3 px-3"><SkeletonLine className="h-3 w-20" /></th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, i) => (
          <tr key={i} className="border-b border-white/[0.04]">
            <td className="py-3 px-3"><SkeletonBlock className="h-4 w-4 rounded" /></td>
            <td className="py-3 px-3">
              <div className="flex items-center gap-3">
                <SkeletonBlock className="h-9 w-9 shrink-0 rounded-full" />
                <div className="flex flex-col gap-1.5">
                  <SkeletonLine className="h-3 w-40" />
                  <SkeletonLine className="h-2.5 w-28" />
                </div>
              </div>
            </td>
            <td className="py-3 px-3"><SkeletonLine className="h-3 w-20" /></td>
            <td className="py-3 px-3"><SkeletonLine className="h-5 w-16 rounded-full" /></td>
            <td className="py-3 px-3"><SkeletonLine className="h-5 w-10 rounded-full" /></td>
            <td className="py-3 px-3">
              <div className="flex justify-end gap-2">
                <SkeletonBlock className="h-7 w-7 rounded-lg" />
                <SkeletonBlock className="h-7 w-7 rounded-lg" />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
export const SkeletonLeadModal: React.FC = () => (
  <div
    className="fixed inset-0 z-[10010] flex items-center justify-center p-2 sm:p-3 md:p-4 lg:p-5"
    role="status"
    aria-busy="true"
    aria-label="Loading lead details"
  >
    {/* Backdrop */}
    <div className="absolute inset-0 bg-[rgba(3,6,12,0.82)]" aria-hidden="true" />

    {/* Modal panel */}
    <div className="relative flex h-[95vh] max-h-[95vh] w-full max-w-[1640px] flex-col overflow-hidden rounded-[24px] border border-white/8 bg-[linear-gradient(180deg,rgba(14,17,24,0.98),rgba(7,9,14,0.99))] shadow-[0_32px_90px_rgba(0,0,0,0.5)]">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] px-5 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="h-10 w-10 rounded-xl" />
          <div className="flex flex-col gap-1.5">
            <SkeletonLine className="h-4 w-48" />
            <SkeletonLine className="h-3 w-32" />
          </div>
        </div>
        <SkeletonBlock className="h-9 w-9 rounded-lg" />
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-6 border-b border-white/8 px-5 py-2.5 sm:px-6" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLine key={i} className="h-3 w-20" />
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
        <SkeletonSection lines={4} />
        <SkeletonSection lines={3} />
        <SkeletonSection lines={5} />
        <SkeletonSection lines={3} />
      </div>
    </div>
  </div>
);

export default SkeletonGrid;
